/**
 * 铃铛通知入口 - 消息中心面板
 * @author ritaxxu
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Tabs, Empty, Spin, message } from 'antd'
import { BellOutlined } from '@ant-design/icons'
import { useShallow } from 'zustand/react/shallow'
import { notificationStore } from '@store'
import * as notificationService from '@services/notification'
import {
  IInnerMsgItem,
  InnerMsgLevel,
  InnerMsgReadStatus,
  IGetInnerMsgListReq,
} from '@types'
import MessageCard from './MessageCard'
import MessageDetailModal from './MessageDetailModal'
import './index.less'

type TabKey = 'all' | 'unread' | 'read' | 'important'

const NotificationBell: React.FC = () => {
  const {
    unreadCount,
    panelVisible,
    togglePanel,
    closePanel,
    fetchUnreadCount,
    startPolling,
  } = notificationStore(
    useShallow((s) => ({
      unreadCount: s.unreadCount,
      panelVisible: s.panelVisible,
      togglePanel: s.togglePanel,
      closePanel: s.closePanel,
      fetchUnreadCount: s.fetchUnreadCount,
      startPolling: s.startPolling,
    })),
  )

  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [messages, setMessages] = useState<IInnerMsgItem[]>([])
  const [loading, setLoading] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentMessage, setCurrentMessage] = useState<IInnerMsgItem | null>(null)

  // 消息类别选项，value 与后端返回的 tag 文案一致，'all' 表示不过滤
  const categoryOptions = [
    { value: 'all', label: '全部' },
    { value: '线索派发', label: '线索派发' },
    { value: '客户分配', label: '客户分配' },
    { value: '客户回收', label: '客户回收' },
    { value: '线索通知', label: '线索通知' },
  ]

  // 按类目对当前消息列表做本地过滤
  const filteredMessages
    = activeCategory === 'all'
      ? messages
      : messages.filter((m) => m.tags?.includes(activeCategory))

  // 启动轮询
  useEffect(() => {
    startPolling()
  }, [startPolling])

  // 面板打开时加载消息，同时把类目筛选重置为「全部」
  useEffect(() => {
    if (panelVisible) {
      setActiveCategory('all')
      loadMessages(activeTab)
    }
  }, [panelVisible])

  // 切换 tab 时也把类目筛选重置为「全部」，并重新拉列表
  useEffect(() => {
    if (!panelVisible) return
    setActiveCategory('all')
    loadMessages(activeTab)
  }, [activeTab])

  const loadMessages = async (tab: TabKey) => {
    setLoading(true)
    const params: IGetInnerMsgListReq = { page: 1, pageSize: 20 }
    if (tab === 'unread') {
      params.readStatus = InnerMsgReadStatus.Unread
    } else if (tab === 'read') {
      params.readStatus = InnerMsgReadStatus.Read
    } else if (tab === 'important') {
      params.readStatus = InnerMsgReadStatus.Unread
      params.level = InnerMsgLevel.Important
    }
    const [data] = await notificationService.getInnerMsgList(params)
    setMessages(data?.list || [])
    setLoading(false)
  }

  // 标为已读：普通/重要都走 MarkInnerMsgRead(recipientIds=[id])
  const doMarkRead = useCallback(async (msg: IInnerMsgItem) => {
    await notificationService.markInnerMsgRead({ recipientIds: [msg.id] })
    fetchUnreadCount()
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id ? { ...m, readStatus: InnerMsgReadStatus.Read } : m,
      ),
    )
  }, [fetchUnreadCount])

  // 点击卡片 -> 弹窗详情；普通消息自动已读，重要消息需要用户手动点【标为已读】
  const handleClickMessage = useCallback(async (msg: IInnerMsgItem) => {
    setCurrentMessage(msg)
    setDetailVisible(true)
    if (msg.readStatus === InnerMsgReadStatus.Unread && msg.level === InnerMsgLevel.Normal) {
      await doMarkRead(msg)
    }
  }, [doMarkRead])

  // 卡片右侧【标为已读】按钮 / 详情弹窗【标为已读】按钮
  const handleMarkAsRead = useCallback(async (msg: IInnerMsgItem) => {
    await doMarkRead(msg)
    // 如果详情弹窗展示的是同一条，同步更新弹窗内的状态
    setCurrentMessage((prev) =>
      prev && prev.id === msg.id
        ? { ...prev, readStatus: InnerMsgReadStatus.Read, readTime: new Date().toLocaleString() }
        : prev,
    )
  }, [doMarkRead])

  // 清除普通未读（后端强制 level=1，重要消息不允许批量清除）
  const handleClearNormalUnread = useCallback(async () => {
    await notificationService.markInnerMsgRead({ level: InnerMsgLevel.Normal })
    fetchUnreadCount()
    message.success('已清除普通未读消息')
    loadMessages(activeTab)
  }, [activeTab, fetchUnreadCount])

  const tabItems = [
    { key: 'all', label: '全部' },
    {
      key: 'unread',
      label: (
        <span>
          未读
          {unreadCount.unreadCount > 0 && (
            <Badge count={unreadCount.unreadCount} size="small" style={{ marginLeft: 4 }} />
          )}
        </span>
      ),
    },
    { key: 'read', label: '已读' },
    {
      key: 'important',
      label: (
        <span>
          重要未读
          {unreadCount.importantUnreadCount > 0 && (
            <Badge count={unreadCount.importantUnreadCount} size="small" style={{ marginLeft: 4 }} />
          )}
        </span>
      ),
    },
  ]

  return (
    <div className="notification-bell-wrapper">
      <Badge count={unreadCount.unreadCount} offset={[-4, 4]}>
        <BellOutlined className="notification-bell-icon" onClick={togglePanel} />
      </Badge>

      {panelVisible && (
        <>
          <div className="notification-mask" onClick={closePanel} />
          <div className="notification-panel">
            <div className="notification-panel-header">
              <h3>消息中心</h3>
              <Button type="link" size="small" onClick={handleClearNormalUnread}>
                清除普通未读
              </Button>
            </div>

            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as TabKey)}
              items={tabItems}
              size="small"
              className="notification-tabs"
            />

            <div className="notification-category-filter">
              {categoryOptions.map((cat) => (
                <Button
                  key={cat.value}
                  size="small"
                  shape="round"
                  type={activeCategory === cat.value ? 'primary' : 'default'}
                  onClick={() => setActiveCategory(cat.value)}
                >
                  {cat.label}
                </Button>
              ))}
            </div>

            <div className="notification-panel-body">
              <Spin spinning={loading}>
                {filteredMessages.length === 0 ? (
                  <Empty description="暂无消息" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  filteredMessages.map((msg) => (
                    <MessageCard
                      key={msg.id}
                      message={msg}
                      onClick={() => handleClickMessage(msg)}
                      onMarkAsRead={() => handleMarkAsRead(msg)}
                    />
                  ))
                )}
              </Spin>
            </div>
          </div>
        </>
      )}

      <MessageDetailModal
        visible={detailVisible}
        message={currentMessage}
        onClose={() => setDetailVisible(false)}
        onMarkRead={() => {
          if (currentMessage) {
            handleMarkAsRead(currentMessage)
          }
        }}
      />
    </div>
  )
}

export default NotificationBell
